import { useEffect, useMemo, useState } from "react";
import { Icon } from "../icons";
import {
  createLocalBridgeCommand,
  listLocalBridgeConnections,
  listLocalBridgeResidents,
  type LocalBridgeConnection,
  type LocalBridgeResident,
} from "../localBridgeApi";
import {
  directMessageValidation,
  enabledPaseFirmeResidents,
  readyPaseFirmeConnections,
} from "../paseFirmeWhatsApp";
import type { AuthSession } from "../types";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

export function PaseFirmeNewWhatsAppModal({
  open,
  session,
  organizationName,
  onClose,
  notify,
}: {
  open: boolean;
  session: AuthSession;
  organizationName: string;
  onClose: () => void;
  notify: Notify;
}) {
  const [connections, setConnections] = useState<LocalBridgeConnection[]>([]);
  const [residents, setResidents] = useState<LocalBridgeResident[]>([]);
  const [connectionId, setConnectionId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState("");

  const availableConnections = useMemo(() => readyPaseFirmeConnections(connections), [connections]);
  const enabledResidents = useMemo(() => enabledPaseFirmeResidents(residents), [residents]);
  const availableCustomers = useMemo(() => {
    const items = new Map<string, string>();
    for (const resident of enabledResidents) {
      if (resident.customerId && resident.customer) items.set(resident.customerId, resident.customer);
    }
    return [...items].sort((a, b) => a[1].localeCompare(b[1], "es"));
  }, [enabledResidents]);
  const customerResidents = useMemo(
    () => enabledResidents.filter((resident) => resident.customerId === customerId),
    [customerId, enabledResidents],
  );
  const selectedConnection = availableConnections.find((item) => item.connectionId === connectionId);
  const selectedResident = customerResidents.find((item) => item.phone === recipientPhone);
  const validationError = directMessageValidation({ connectionId, recipientPhone, body });

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError("");
    setReviewing(false);
    void listLocalBridgeConnections(session)
      .then((items) => {
        if (!active) return;
        const ready = readyPaseFirmeConnections(items);
        setConnections(items);
        setConnectionId(ready[0]?.connectionId ?? "");
      })
      .catch((cause) => active && setError(errorMessage(cause)))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [open, session]);

  useEffect(() => {
    if (!open || !connectionId) {
      setResidents([]);
      setCustomerId("");
      setRecipientPhone("");
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    void listLocalBridgeResidents(connectionId, session)
      .then((items) => {
        if (!active) return;
        const enabled = enabledPaseFirmeResidents(items);
        setResidents(items);
        setCustomerId(enabled[0]?.customerId ?? "");
        setRecipientPhone(enabled[0]?.phone ?? "");
      })
      .catch((cause) => active && setError(errorMessage(cause)))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [connectionId, open, session]);

  useEffect(() => {
    if (!customerId) return;
    const first = enabledResidents.find((resident) => resident.customerId === customerId);
    setRecipientPhone((current) => enabledResidents.some((resident) => resident.customerId === customerId && resident.phone === current)
      ? current
      : first?.phone ?? "");
  }, [customerId, enabledResidents]);

  if (!open) return null;

  function close() {
    if (busy) return;
    setReviewing(false);
    setError("");
    onClose();
  }

  async function send() {
    if (validationError || busy) return;
    setBusy(true);
    setError("");
    try {
      await createLocalBridgeCommand(connectionId, {
        type: "send_direct_text",
        recipientPhone,
        body: body.trim(),
      }, session);
      notify("success", `WhatsApp encolado para ${selectedResident?.displayName ?? "el residente"}. Quedará registrado en la conversación.`);
      setBody("");
      setReviewing(false);
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={close}>
      <section className="modal pasefirme-whatsapp-modal" role="dialog" aria-modal="true" aria-labelledby="new-whatsapp-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="modal-close" disabled={busy} onClick={close} type="button" aria-label="Cerrar"><Icon name="x" size={18} /></button>
        <div className="auth-brand"><span><Icon name="whatsapp" size={20} /></span><div><strong>Conversa Smart</strong><small>{organizationName} · residentes autorizados</small></div></div>
        <p className="section-kicker">NUEVO WHATSAPP</p>
        <h2 id="new-whatsapp-title">Iniciar conversación con un residente</h2>
        <p className="modal-description">Sólo se muestran residentes habilitados y con consentimiento registrado. Nada se enviará hasta revisar y confirmar el mensaje.</p>

        {loading ? <div className="loading-line"><span /> Verificando chip y residentes…</div> : null}
        {error ? <div className="composer-gate warning"><strong>No pudimos preparar el envío</strong><span>{error}</span></div> : null}
        {!loading && !availableConnections.length ? <div className="composer-gate warning"><strong>No hay un chip PaseFirme disponible</strong><span>La conexión debe estar activa y en estado conectado antes de iniciar un WhatsApp.</span></div> : null}
        {!loading && availableConnections.length && !enabledResidents.length ? <div className="composer-gate warning"><strong>No hay residentes listos para conversar</strong><span>Asigna cada residente a un cliente/condominio y registra su consentimiento desde Canales.</span></div> : null}

        {availableConnections.length && enabledResidents.length && !reviewing ? (
          <div className="auth-form pasefirme-message-form">
            {availableConnections.length > 1 ? <label className="field-label"><span>Chip de envío</span><select value={connectionId} onChange={(event) => { setConnectionId(event.target.value); setReviewing(false); }}><option value="">Selecciona una conexión</option>{availableConnections.map((connection) => <option key={connection.connectionId} value={connection.connectionId}>{connection.label}{connection.phone ? ` · +${connection.phone}` : ""}</option>)}</select></label> : selectedConnection ? <div className="new-whatsapp-source"><span><Icon name="whatsapp" size={17} /></span><div><strong>{selectedConnection.label}</strong><small>{selectedConnection.phone ? `+${selectedConnection.phone}` : "Chip conectado"}</small></div><em>Conectado</em></div> : null}
            <label className="field-label"><span>Cliente / condominio</span><select value={customerId} onChange={(event) => { const nextCustomerId = event.target.value; setCustomerId(nextCustomerId); setRecipientPhone(enabledResidents.find((resident) => resident.customerId === nextCustomerId)?.phone ?? ""); }}><option value="">Selecciona un cliente</option>{availableCustomers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>
            <label className="field-label"><span>Residente autorizado</span><select value={recipientPhone} onChange={(event) => setRecipientPhone(event.target.value)}><option value="">Selecciona un residente</option>{customerResidents.map((resident) => <option key={resident.phone} value={resident.phone}>{resident.displayName} · +{resident.phone}{resident.facilityId ? ` · ${resident.facilityId}` : ""}</option>)}</select></label>
            <label className="field-label"><span>Mensaje</span><textarea maxLength={4_096} rows={6} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Escribe un mensaje claro para el residente…" /></label>
            <div className="new-whatsapp-character-count">{body.length}/4096</div>
            <button className="button primary" disabled={Boolean(validationError) || !selectedResident || loading} onClick={() => setReviewing(true)} type="button"><Icon name="check" size={16} /> Revisar envío</button>
          </div>
        ) : null}

        {reviewing && selectedConnection && selectedResident ? (
          <div className="new-whatsapp-review">
            <div><span>Se enviará desde</span><strong>{selectedConnection.label}{selectedConnection.phone ? ` · +${selectedConnection.phone}` : ""}</strong></div>
            <div><span>Cliente / condominio</span><strong>{selectedResident.customer}</strong><small>{selectedResident.site}</small></div>
            <div><span>Destinatario</span><strong>{selectedResident.displayName} · +{selectedResident.phone}</strong><small>{selectedResident.facilityId}</small></div>
            <blockquote>{body.trim()}</blockquote>
            <p><Icon name="check" size={15} /> El destinatario pertenece a la lista blanca de PaseFirme.</p>
            <div className="connection-actions"><button className="button soft" disabled={busy} onClick={() => setReviewing(false)} type="button">Volver y editar</button><button className="button primary" disabled={busy} onClick={() => void send()} type="button"><Icon name="whatsapp" size={16} /> {busy ? "Encolando…" : "Confirmar y enviar"}</button></div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function errorMessage(cause: unknown) {
  const code = cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO";
  const messages: Record<string, string> = {
    BRIDGE_CONNECTION_INACTIVE: "El chip está pausado o desconectado.",
    BRIDGE_CAPTURE_MODE_MISMATCH: "La conexión seleccionada no corresponde a residentes PaseFirme.",
    RESIDENT_NOT_ENABLED: "El residente ya no está habilitado para recibir mensajes.",
    RESIDENT_CUSTOMER_REQUIRED: "El residente debe estar asignado a un cliente y condominio.",
  };
  return messages[code] ?? code.replaceAll("_", " ").toLocaleLowerCase("es-CL");
}
